import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Star, Quote, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.16, 1, 0.3, 1];

function Stars({ value, size = 15 }) {
  return (
    <div className="flex" aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          className={i <= value ? "fill-[#C1440E] text-[#C1440E]" : "text-[#C68A3A]"}
        />
      ))}
    </div>
  );
}

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", rating: 0, text: "" });
  const [hover, setHover] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchReviews = async () => {
    try {
      const { data } = await axios.get(`${API}/reviews`);
      setReviews(data);
    } catch (e) {
      /* silent */
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const avg = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : "5.0";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.text || form.rating < 1) {
      toast.error("Please add your name, a rating and a few words.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/reviews`, form);
      setReviews((prev) => [data, ...prev]);
      setForm({ name: "", rating: 0, text: "" });
      setHover(0);
      toast.success("Thank you! Your review is now live.");
    } catch (err) {
      toast.error("Couldn't post your review. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#C68A3A] py-3 text-[#241B2E] placeholder:text-[#5A2E14]/50 focus:outline-none focus:border-[#C1440E] transition-colors duration-300";

  return (
    <section id="reviews" className="px-6 py-24 md:py-36" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-2xl"
          >
            Kind words from <span className="italic text-[#C1440E]">happy homes</span>
          </motion.h2>
          <div className="flex items-center gap-3" data-testid="reviews-average">
            <span className="font-serif text-5xl text-[#C1440E] leading-none">{avg}</span>
            <div>
              <Stars value={Math.round(avg)} />
              <p className="text-sm text-[#5A2E14] mt-1">{reviews.length} review{reviews.length === 1 ? "" : "s"}</p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          {/* Submit form */}
          <motion.form
            onSubmit={submit}
            data-testid="review-form"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease }}
            className="rounded-3xl border border-[#C68A3A] bg-[#241B2E] text-[#EFD9AE] p-8 flex flex-col lg:sticky lg:top-28 h-fit"
          >
            <h3 className="font-serif text-2xl md:text-3xl tracking-tight">Leave a review</h3>
            <p className="text-white/60 text-sm mt-2 mb-6">Cleaned with us? Tell everyone how it went.</p>

            <label className="text-sm text-white/70 mb-2">Your rating</label>
            <div className="flex gap-1.5 mb-5" data-testid="review-rating-input">
              {[1, 2, 3, 4, 5].map((i) => (
                <button
                  type="button"
                  key={i}
                  data-testid={`review-star-${i}`}
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(0)}
                  onClick={() => setForm((f) => ({ ...f, rating: i }))}
                  className="p-0.5"
                  aria-label={`Rate ${i} stars`}
                >
                  <Star
                    size={26}
                    className={`transition-colors duration-200 ${
                      i <= (hover || form.rating) ? "fill-[#C1440E] text-[#C1440E]" : "text-white/30"
                    }`}
                  />
                </button>
              ))}
            </div>

            <input
              data-testid="review-name-input"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Your name"
              className="w-full bg-transparent border-b border-white/25 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-[#C1440E] transition-colors duration-300 mb-5"
            />
            <textarea
              data-testid="review-text-input"
              value={form.text}
              onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
              rows={4}
              placeholder="What did you love about the clean?"
              className="w-full bg-transparent border-b border-white/25 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-[#C1440E] transition-colors duration-300 resize-none mb-7"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="review-submit-btn"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#C1440E] text-white hover:-translate-y-1 transition-transform duration-300 disabled:opacity-60 disabled:hover:translate-y-0"
            >
              <span className="text-sm font-medium">{loading ? "Posting..." : "Post review"}</span>
              {loading && <Loader2 size={16} className="animate-spin" />}
            </button>
          </motion.form>

          {/* Reviews list */}
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-5 auto-rows-max" data-testid="reviews-list">
            {reviews.map((r, i) => (
              <motion.figure
                key={r.id}
                data-testid={`review-card-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 4) * 0.06, ease }}
                className="rounded-3xl border border-[#C68A3A] bg-[#F7E9CC] p-7 flex flex-col justify-between hover:-translate-y-1.5 transition-transform duration-300 h-fit"
              >
                <div>
                  <div className="flex items-center justify-between">
                    <Stars value={r.rating} />
                    <Quote className="text-[#C1440E]" size={22} />
                  </div>
                  <blockquote className="font-serif text-xl leading-snug mt-4 text-[#241B2E]">
                    "{r.text}"
                  </blockquote>
                </div>
                <figcaption className="mt-6 pt-5 border-t border-[#C68A3A]/60 font-medium text-[#241B2E]">
                  {r.name}
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
